import { motion, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"

/**
 * El telón del acceso y del sistema: el puerto en video, en bucle de 24 s.
 *
 * Son las cuatro fotografías que tuvo el carrusel —la terminal de noche, el patio,
 * el muelle y la vista aérea— animadas con HyperFrames: luces que respiran,
 * reflejos en el agua, bruma, paralaje y sombras de nubes, con la gradación de la
 * marca (sombras al vino, luces al dorado del filete). El proyecto que lo genera
 * vive fuera del repo, en `login-aduanal3-animacion`.
 *
 * ⚠ **Es telón, no cartel**, igual que lo era el carrusel: sin texto, sin logos y
 * sin nada que pulsar. Lo único que se lee en esta pantalla es el formulario.
 * Los puntos del carrusel se fueron con él: no hay tomas entre las que saltar.
 *
 * ⚠ **El bucle no tiene costura.** El último fotograma empalma con el primero (se
 * midió: difieren en 1–2 niveles de 255), y el primero es la toma nocturna quieta
 * —la más oscura—, así que la lámina se recorta bien desde el primer instante,
 * como pedía el carrusel con su foto de noche delante.
 *
 * ⚠ **Se sirve desde `public/`, nunca desde un CDN.** La agencia trabaja en Puerto
 * Cabello: el día que se caiga el enlace de salida, la gente tiene que poder entrar
 * igual. Y el nombre lleva versión (`-v1`): los archivos de `public/` no llevan
 * hash, así que un video nuevo con el mismo nombre seguiría saliendo viejo de la
 * caché de quien ya lo vio.
 */

/** Los archivos, en `public/fondos/`. */
const VIDEO = "/fondos/fondo-acceso-v1"
/** El fotograma 0 del bucle: lo que se ve mientras carga y sin movimiento. */
export const POSTER = `${VIDEO}-poster.jpg`

/**
 * Dentro del sistema el video va a la MITAD de velocidad.
 *
 * ⚠ **Sigue vivo, y es una decisión del usuario (11/10/2026).** Hubo una propuesta
 * de congelarlo al entrar —un video detrás del cristal todo el día cuesta CPU— y
 * se descartó por lo mismo que se descartó congelar las fotos: una imagen quieta
 * detrás del sistema deja de ser el puerto y pasa a ser un cuadro. Lo que se
 * conserva de aquel razonamiento es el RITMO: a media velocidad y apagado, el ojo
 * no encuentra un cambio cada vez que levanta la vista de una tabla.
 */
const VELOCIDAD_SISTEMA = 0.5

/**
 * La versión ligera (960×540, la mitad de peso) para un teléfono o quien pidió
 * ahorrar datos. Se decide UNA vez al montar: cambiar de fuente a mitad de bucle
 * reinicia el video, y girar el teléfono no justifica ese salto.
 */
function usarLigera(): boolean {
  if (typeof window === "undefined") return false
  const conexion = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  return Boolean(conexion?.saveData) || window.matchMedia?.("(max-width: 767px)").matches === true
}

export function FondoPuerto({
  atenuado = false,
}: {
  /** Dentro del sistema: el fondo baja de intensidad y el video va más lento. */
  atenuado?: boolean
}) {
  // Quien pidió al sistema que no le muevan la pantalla ve el póster: es una
  // fotografía fija y sigue siendo una buena pantalla.
  const quieto = useReducedMotion()
  const video = useRef<HTMLVideoElement>(null)
  const [listo, setListo] = useState(false)
  const [ligera] = useState(usarLigera)

  // ⚠ **`muted` se fija por la PROPIEDAD y el `play()` se pide a mano.** React no
  // escribe el atributo `muted` en el HTML, y sin él varios navegadores niegan la
  // reproducción automática aunque el JSX diga `autoPlay`.
  useEffect(() => {
    const v = video.current
    if (!v) return
    v.muted = true
    try {
      v.play()?.catch(() => {})
    } catch {
      // jsdom y navegadores sin códecs: queda el póster, que es una buena pantalla.
    }
  }, [])

  // ⚠ **La velocidad se reaplica al cargar**: un `<video>` vuelve a 1× cada vez
  // que carga una fuente, así que fijarla sólo en el efecto la perdería.
  const velocidad = atenuado ? VELOCIDAD_SISTEMA : 1
  const aplicarVelocidad = () => {
    if (video.current) video.current.playbackRate = velocidad
  }
  useEffect(() => {
    if (video.current) video.current.playbackRate = velocidad
  }, [velocidad])

  return (
    <div className="no-print pointer-events-none absolute inset-0 overflow-hidden bg-[var(--acceso-fondo)]">
      <div className="absolute inset-0" aria-hidden="true">
        {/* El póster va SIEMPRE debajo: si el video tarda o no puede reproducirse,
            la pantalla ya está vestida, y el fundido del video arranca sobre la
            misma imagen —es su fotograma 0— en vez de sobre un hueco. */}
        <img src={POSTER} alt="" className="absolute inset-0 size-full object-cover" />
        {!quieto && (
          <motion.video
            ref={video}
            className="absolute inset-0 size-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={POSTER}
            onLoadedMetadata={aplicarVelocidad}
            onPlaying={() => setListo(true)}
            initial={false}
            animate={{ opacity: listo ? 1 : 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <source src={`${VIDEO}${ligera ? "-960" : ""}.webm`} type="video/webm" />
            <source src={`${VIDEO}${ligera ? "-960" : ""}.mp4`} type="video/mp4" />
          </motion.video>
        )}

        {/*
          UN VELO SUAVE Y NADA MÁS. El contraste no se arregla oscureciendo la
          pantalla entera —eso mata la imagen y deja un rectángulo con formas
          dentro—: se arregla donde ocurre, y donde ocurre es dentro de la lámina,
          que tiene su propio tinte y su propio borde. Aquí sólo se asientan los
          bordes. Vino y no azul: es el color de la marca.
        */}
        <div className="absolute inset-0 bg-gradient-to-b from-[rgb(var(--acceso-velo)/0.35)] via-[rgb(var(--acceso-velo)/0.15)] to-[rgb(var(--acceso-velo)/0.45)]" />

        {/*
          El apagado del fondo al entrar en el sistema.

          ⚠ **Va DENTRO de este grupo y sin `z-index`.** Estuvo suelta arriba con
          `z-10` y dejó la aplicación entera en negro: este telón no crea contexto
          de apilamiento, así que un hijo con `z-10` no se apila «dentro del fondo»
          sino contra la página, y le gana al contenido. Aquí, la última del grupo y
          sin z, queda sobre el video y por debajo de todo lo que pinte la
          aplicación.

          ⚠ **El 0,25 es lo que queda DESPUÉS del marco**: encima va el cristal del
          menú y del encabezado con su propio tinte, y las transparencias se
          multiplican. Tarda 1,6 s —más que la transformación de la lámina— para
          que lo último que ocurra sea que la pantalla se asienta.
        */}
        <motion.div
          className="absolute inset-0 bg-[var(--acceso-fondo)]"
          initial={false}
          animate={{ opacity: atenuado ? 0.25 : 0 }}
          transition={{ duration: 1.6, ease: "easeInOut" }}
        />

        {/*
          ⚠ **La sombra negra al 30 % va SÓLO dentro del sistema**, con el mismo
          fundido que el apagado, para que la pantalla se asiente de una vez. En el
          acceso existe a opacidad cero: si no, al entrar no habría desde dónde
          fundirla. El color vive en `--fondo-sombra`, que se ajusta en Apariencia.
        */}
        <motion.div
          data-testid="sombra-fondo"
          className="absolute inset-0 bg-[var(--fondo-sombra)]"
          initial={false}
          animate={{ opacity: atenuado ? 1 : 0 }}
          transition={{ duration: 1.6, ease: "easeInOut" }}
        />
      </div>
    </div>
  )
}
